import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    WorkOrderChangeOtherPriceCalcInput,
    WorkOrderChangeOtherPriceCostData,
    WorkOrderOtherPriceCalcInput,
    WorkOrderOtherPriceCostData,
} from 'gql/graphql';
import { useDebounce } from 'uikit';

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
    calcWorkOrderDistributionGql,
    uerCalcWorkChangeOrderOtherDistributionGql,
} from '../../EditOtherDistribution/EditOtherDistributionQueries';

const requestId = 'uerCalcWorkOrderOtherDistribution';

type TWorkOrderCalcDistribution = {
    workOrderOtherPriceId: number | undefined;
    onCalcSuccess: (
        data: (WorkOrderOtherPriceCostData | WorkOrderChangeOtherPriceCostData | null)[] | undefined | null,
    ) => void;
    onCalcError: () => void;
    isWorkOrderChange: boolean;
    workOrderChangeCompositeIds: string | undefined;
};

const toNumberOrNull = (value: string) => (value ? Number(value) : null);

export const useUerCalcWorkOrderDistribution = (props: TWorkOrderCalcDistribution) => {
    const {
        workOrderOtherPriceId,
        onCalcSuccess,
        onCalcError,
        isWorkOrderChange,
        workOrderChangeCompositeIds = '',
    } = props;

    const dispatch = useDispatch();

    const [data, status, , request] = useRequest({
        requestId,
        query: calcWorkOrderDistributionGql,
        withEffect: false,
    });
    const [dataWorkOrderChange, statusWorkOrderChange, , requestWorkOrderChange] = useRequest({
        requestId,
        query: uerCalcWorkChangeOrderOtherDistributionGql,
        withEffect: false,
    });

    const [calcWorkOrderOtherDistributionReset] = useResetStoreData({
        requestId,
    });

    useEffect(() => {
        if (
            (status === RequestStatus.SUCCESS && data?.uerCalcWorkOrderOtherDistribution?.result) ||
            (dataWorkOrderChange?.uerCalcWorkOrderChangeOtherDistribution?.result &&
                statusWorkOrderChange === RequestStatus.SUCCESS)
        ) {
            if (isWorkOrderChange) {
                onCalcSuccess(dataWorkOrderChange?.uerCalcWorkOrderChangeOtherDistribution?.data);
            } else {
                onCalcSuccess(data?.uerCalcWorkOrderOtherDistribution?.data);
            }

            calcWorkOrderOtherDistributionReset(undefined);
        }
    }, [
        calcWorkOrderOtherDistributionReset,
        data?.uerCalcWorkOrderOtherDistribution?.result,
        data?.uerCalcWorkOrderOtherDistribution?.data,
        status,
        onCalcSuccess,
        dataWorkOrderChange?.uerCalcWorkOrderChangeOtherDistribution?.result,
        statusWorkOrderChange,
        dataWorkOrderChange?.uerCalcWorkOrderChangeOtherDistribution?.data,
        isWorkOrderChange,
    ]);

    useEffect(() => {
        if (
            (!isWorkOrderChange &&
                (status === RequestStatus.ERROR ||
                    (status === RequestStatus.SUCCESS && !data?.uerCalcWorkOrderOtherDistribution?.result))) ||
            (isWorkOrderChange &&
                (statusWorkOrderChange === RequestStatus.ERROR ||
                    (statusWorkOrderChange === RequestStatus.SUCCESS &&
                        !dataWorkOrderChange?.uerCalcWorkOrderChangeOtherDistribution?.result)))
        ) {
            onCalcError();

            calcWorkOrderOtherDistributionReset(undefined);
        }
    }, [
        calcWorkOrderOtherDistributionReset,
        data?.uerCalcWorkOrderOtherDistribution?.result,
        dataWorkOrderChange?.uerCalcWorkOrderChangeOtherDistribution?.result,
        isWorkOrderChange,
        onCalcError,
        status,
        statusWorkOrderChange,
    ]);

    const calcWorkOrderDistributionRequest = useCallback(
        (input: WorkOrderOtherPriceCalcInput | WorkOrderChangeOtherPriceCalcInput) => {
            if (isWorkOrderChange) {
                // по аналогии с БИМ. композитный ключ
                // workOrderChangeId_workOrderOtherPriceId_workOrderChangeOtherPriceId
                const [workOrderChangeId, compWorkOrderOtherPriceId, workOrderChangeOtherPriceId] =
                    workOrderChangeCompositeIds.split('_');

                dispatch(
                    requestWorkOrderChange({
                        workOrderChangeId: Number(workOrderChangeId),
                        workOrderOtherPriceId: toNumberOrNull(compWorkOrderOtherPriceId),
                        workOrderChangeOtherPriceId: toNumberOrNull(workOrderChangeOtherPriceId),
                        input: input as WorkOrderChangeOtherPriceCalcInput,
                    }),
                );
            } else {
                dispatch(
                    request({
                        workOrderOtherPriceId,
                        input: input as WorkOrderOtherPriceCalcInput,
                    }),
                );
            }
        },
        [
            dispatch,
            isWorkOrderChange,
            request,
            requestWorkOrderChange,
            workOrderChangeCompositeIds,
            workOrderOtherPriceId,
        ],
    );

    const debouncedChange = useDebounce(calcWorkOrderDistributionRequest, 700);

    return {
        calcWorkOrderDistributionRequest: debouncedChange,
        calcWorkOrderDistributionStatus: status,
        calcInProgress: status === RequestStatus.FETCHING,
    };
};
